import type { AccessCatalogItemPublic, AccessNodePublic, ApplicationPublic } from '@accounts/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { STR } from '../../../../i18n';
import { createAccessCatalogItem } from '../../../../lib/api';
import { showToast } from '../../../../lib/toast';
import { Modal, ModalForm } from '../../../../ui/Modal';
import { Callout } from '../../../../ui/Primitives';
import { modalButtonStyle } from '../../../../ui/styles';
import { itemLabel } from '../accessFormat';

import { errorText, useInvalidateAccess } from './queries';
import { type RowDraft, RowFields } from './RowFields';

/**
 * Add catalog row. Rows start requestable (the demo's default: a row someone took the
 * trouble to add is usually one people should be able to ask for) and approved by
 * administrators; HR-can-grant starts off, so giving access without a request is always a
 * deliberate choice.
 */
export function AddCatalogRowDialog({
  nodes,
  applications,
  onClose,
  onAdded,
}: {
  nodes: readonly AccessNodePublic[];
  applications: readonly ApplicationPublic[];
  onClose: () => void;
  onAdded: (item: AccessCatalogItemPublic) => void;
}) {
  const invalidate = useInvalidateAccess();
  const [draft, setDraft] = useState<RowDraft>({
    name: '',
    nodeId: null,
    applicationId: null,
    requestable: true,
    hrCanGrant: false,
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createAccessCatalogItem({
        name: draft.name.trim(),
        nodeId: draft.nodeId,
        applicationId: draft.applicationId,
        requestable: draft.requestable,
        hrCanGrant: draft.hrCanGrant,
      }),
    onSuccess: async (response) => {
      await invalidate();
      showToast(STR.access.catalog.rowAdded(itemLabel(response.item.path, response.item.name)));
      onAdded(response.item);
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  const place = nodes.find((node) => node.id === draft.nodeId);
  const preview = itemLabel(place?.path ?? [], draft.name.trim() === '' ? STR.access.catalog.namePending : draft.name.trim());

  function submit() {
    if (draft.name.trim() === '') {
      setError(STR.access.catalog.nameRequired);
      return;
    }
    setError(null);
    create.mutate();
  }

  return (
    <Modal
      title={STR.access.catalog.addTitle}
      lead={STR.access.catalog.addLead}
      width="min(620px,100%)"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={create.isPending}
            onClick={submit}
          >
            {STR.access.catalog.addConfirm}
          </button>
        </>
      }
    >
      <ModalForm>
        <RowFields
          draft={draft}
          onChange={(next) => {
            setDraft(next);
            setError(null);
          }}
          nodes={nodes}
          applications={applications}
          idPrefix="add-row"
        />
        <div className="tx-subtle" style={{ fontSize: 12 }}>
          {STR.access.catalog.willBeCalled(preview)}
        </div>
        {error === null ? null : (
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        )}
      </ModalForm>
    </Modal>
  );
}
